import React from 'react';
import { Transaction } from '../../types';
import { useCurrency } from '../../context/CurrencyContext';
import { formatAmount, formatDateShort, formatExactPrice } from '../../utils/formatters';
import { TotalCell, TypePill, WalletCell, relativeTime } from '../Transactions/txnHelpers';
import { txPriceInDisplay } from '../../utils/displayAmount';
import { Receipt } from 'lucide-react';

/**
 * Read-only transaction history for a single coin on the Coin Detail page.
 *
 * This is a filtered view of data the app already holds (PortfolioContext's
 * transactions) — no refetch, no recompute. It mirrors AllTransactionsTable's
 * columns/formatting for consistency, minus the redundant Asset column (every
 * row is this coin) and the edit/delete actions (view-only here).
 *
 * `transactions` must already be filtered to this coin and sorted newest-first.
 */
interface Props {
  transactions: Transaction[];
  symbol: string;
}

const TH: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--color-label)',
  fontWeight: 500,
};

function monthLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export function CoinTransactionHistory({ transactions, symbol }: Props) {
  // `fmt` is no longer needed here: totals render through <TotalCell>.
  const { rate, currency } = useCurrency();

  if (transactions.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center text-center py-14 rounded-xl"
        style={{ background: 'rgb(var(--overlay) / 0.02)', border: '0.5px solid rgb(var(--overlay) / 0.06)' }}
      >
        <Receipt className="w-7 h-7 mb-3" style={{ color: 'var(--color-sub)' }} />
        <p className="text-sm" style={{ color: 'var(--color-sub)' }}>
          No transactions yet for {symbol.toUpperCase()}
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--color-sub)', opacity: 0.7 }}>
          Buys, sells and transfers for this coin will appear here.
        </p>
      </div>
    );
  }

  let lastMonth = '';

  // Group by month for the mobile list (same tx objects — no recompute/refetch).
  const monthGroups: { month: string; txns: Transaction[] }[] = [];
  for (const tx of transactions) {
    const m = monthLabel(tx.date);
    const last = monthGroups[monthGroups.length - 1];
    if (last && last.month === m) last.txns.push(tx);
    else monthGroups.push({ month: m, txns: [tx] });
  }

  return (
    <>
      {/* Desktop table (≥ md) — unchanged */}
      <div className="hidden md:block overflow-x-auto">
      <table className="w-full min-w-[620px]">
        <thead>
          <tr style={{ borderBottom: '0.5px solid rgb(var(--overlay) / 0.08)' }}>
            <th className="px-3 py-2.5 pl-0 text-left" style={TH}>Date</th>
            <th className="px-3 py-2.5 text-left" style={TH}>Type</th>
            <th className="px-3 py-2.5 text-right" style={TH}>Amount</th>
            <th className="px-3 py-2.5 text-right" style={TH}>Price</th>
            <th className="px-3 py-2.5 text-right" style={TH}>Total</th>
            <th className="px-3 py-2.5 pr-0 text-left" style={TH}>Wallet</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((tx) => {
            const m = monthLabel(tx.date);
            const showDivider = m !== lastMonth;
            lastMonth = m;
            return (
              <React.Fragment key={tx.id}>
                {showDivider && (
                  <tr>
                    <td colSpan={6} className="pt-5 pb-1.5 pl-0">
                      <div className="flex items-center gap-3">
                        <span
                          className="uppercase font-semibold whitespace-nowrap"
                          style={{ fontSize: 10, letterSpacing: '0.1em', color: '#b89968' }}
                        >
                          {m}
                        </span>
                        <span className="flex-1 h-px" style={{ background: 'rgb(var(--overlay) / 0.06)' }} />
                      </div>
                    </td>
                  </tr>
                )}
                <tr style={{ borderBottom: '0.5px solid rgb(var(--overlay) / 0.04)' }}>
                  {/* Date */}
                  <td className="px-3 py-3 pl-0 whitespace-nowrap">
                    <p className="text-[13px]" style={{ color: 'rgb(var(--sl-100))' }}>{formatDateShort(tx.date)}</p>
                    <p className="text-[10px] mt-0.5" style={{ color: 'var(--color-sub)' }}>{relativeTime(tx.date)}</p>
                  </td>

                  {/* Type */}
                  <td className="px-3 py-3"><TypePill type={tx.type} /></td>

                  {/* Amount */}
                  <td className="px-3 py-3 text-right num text-[13px]" style={{ color: 'rgb(var(--sl-100))' }}>
                    {formatAmount(tx.amount)} <span className="text-[10px]" style={{ color: 'var(--color-sub)' }}>{tx.symbol}</span>
                  </td>

                  {/* Price — user-entered, shown at the exact precision they logged */}
                  <td className="px-3 py-3 text-right num text-[13px]" style={{ color: 'var(--color-sub)' }}>
                    {tx.type === 'TRANSFER' ? '—' : formatExactPrice(txPriceInDisplay(tx, currency.code, rate).value, currency.code)}
                  </td>

                  {/* Total (or transfer network fee) */}
                  <td className="px-3 py-3 text-right num text-[13px] font-medium" style={{ color: 'rgb(var(--sl-100))' }}>
                    <TotalCell tx={tx} />
                  </td>

                  {/* Wallet (from → to for transfers) */}
                  <td className="px-3 py-3 pr-0"><WalletCell tx={tx} /></td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </div>

      {/* Mobile (< md) — compact divided list, month headers preserved, no h-scroll */}
      <div className="md:hidden">
        {monthGroups.map((group) => (
          <div key={group.month}>
            {/* Month header */}
            <div className="flex items-center gap-3 pt-4 pb-2">
              <span
                className="uppercase font-semibold whitespace-nowrap"
                style={{ fontSize: 10, letterSpacing: '0.1em', color: '#b89968' }}
              >
                {group.month}
              </span>
              <span className="flex-1 h-px" style={{ background: 'rgb(var(--overlay) / 0.06)' }} />
            </div>

            {/* Rows */}
            <div className="divide-y divide-slate-700/40">
              {group.txns.map((tx) => (
                <div key={tx.id} className="flex items-start justify-between gap-3 py-3">
                  {/* Left: type badge + date */}
                  <div className="min-w-0">
                    <TypePill type={tx.type} />
                    <p className="text-[12px] mt-1.5" style={{ color: 'rgb(var(--sl-100))' }}>{formatDateShort(tx.date)}</p>
                    <p className="text-[10px] mt-0.5" style={{ color: 'var(--color-sub)' }}>{relativeTime(tx.date)}</p>
                  </div>

                  {/* Right: amount + price */}
                  <div className="text-right flex-shrink-0">
                    <p className="num text-[13px]" style={{ color: 'rgb(var(--sl-100))' }}>
                      {formatAmount(tx.amount)} <span className="text-[10px]" style={{ color: 'var(--color-sub)' }}>{tx.symbol}</span>
                    </p>
                    <p className="num text-[12px] mt-0.5" style={{ color: 'var(--color-sub)' }}>
                      {tx.type === 'TRANSFER' ? '—' : formatExactPrice(txPriceInDisplay(tx, currency.code, rate).value, currency.code)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
